import React, { MouseEventHandler } from 'react';
import { IconName } from '@ardoq/icons';
import { isTruthy } from '@ardoq/common-helpers';
import {
  DividerConfig,
  ExportConfigParams,
  SettingsConfig,
  SettingsType,
} from '@ardoq/view-settings';
import * as settingsBarConsts from '../consts';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import { ViewIds } from '@ardoq/api-types';
import { type I18nLocale, t } from '@ardoq/i18n';

export type SaveAndExportButtonProps = {
  onSaveToPresentationClick?: MouseEventHandler;
  onExportAsPngClick?: MouseEventHandler;
  isExportDisabled?: boolean;
  exportDisabledMessage?: string;
};

export type ViewSettingsBase = {
  [prop: string]: any;
  isLegendActive?: boolean | undefined;
  fullScreen?: boolean | undefined;
};

export interface GetRightMenuConfigParams {
  viewstate: ViewSettingsBase;
  exports: ExportConfigParams;
  withLegend?: boolean;
  knowledgeBaseLink?: string;
  legendOnClick?: React.MouseEventHandler;
  onViewSettingsUpdate: OnViewSettingsUpdate;
  viewId: ViewIds;
  isLegendDisabled?: boolean;
  isFullscreenDisabled?: boolean;
  isExportsDisabled?: boolean;
  exportsDisabledMessage?: string;
  /** additional settings will be placed to the immediate right of the legend toggle.  */
  additionalItems?: SettingsConfig[];
  isPresentationMode?: boolean;
  i18nLocale: I18nLocale;
}

const getRightMenuConfig = ({
  viewstate,
  exports,
  withLegend = true,
  knowledgeBaseLink = '',
  legendOnClick,
  onViewSettingsUpdate,
  isLegendDisabled = false,
  isFullscreenDisabled = false,
  isExportsDisabled = false,
  exportsDisabledMessage,
  additionalItems = [],
  isPresentationMode,
  i18nLocale,
}: GetRightMenuConfigParams): SettingsConfig[] =>
  [
    knowledgeBaseLink && !isPresentationMode
      ? {
          id: 'knowledgeBaseLink',
          label: t(i18nLocale, 'settingsBar:knowledgeBase', 'Knowledge base'),
          iconName: IconName.HELP,
          type: SettingsType.BUTTON,
          onClick: () => window.open(knowledgeBaseLink, '_blank'),
        }
      : null,
    withLegend
      ? {
          id: settingsBarConsts.IS_LEGEND_ACTIVE,
          label: t(i18nLocale, 'settingsBar:toggleLegend', 'Toggle legend'),
          iconName: IconName.INFO,
          isActive: viewstate[settingsBarConsts.IS_LEGEND_ACTIVE],
          isDisabled: isLegendDisabled,
          persistent: false,
          type: SettingsType.TOGGLE,
          onClick: legendOnClick,
          onViewSettingsUpdate,
        }
      : null,
    ...additionalItems,
    additionalItems.length
      ? ({ id: 'divider2', type: SettingsType.DIVIDER } satisfies DividerConfig)
      : null,
    isFullscreenDisabled
      ? null
      : {
          id: settingsBarConsts.FULLSCREEN_KEY,
          label: viewstate[settingsBarConsts.FULLSCREEN_KEY]
            ? t(
                i18nLocale,
                'settingsBar:leaveFullscreen',
                settingsBarConsts.LEAVE_FULLSCREEN
              )
            : t(
                i18nLocale,
                'settingsBar:fullscreen',
                settingsBarConsts.FULLSCREEN
              ),
          isActive: viewstate[settingsBarConsts.FULLSCREEN_KEY],
          iconName: viewstate[settingsBarConsts.FULLSCREEN_KEY]
            ? IconName.FULLSCREEN_EXIT
            : IconName.FULLSCREEN,
          type: SettingsType.TOGGLE,
          persistent: false,
          onViewSettingsUpdate,
        },
    isPresentationMode
      ? null
      : {
          id: 'export',
          label: t(i18nLocale, 'settingsBar:export', 'Export'),
          isDisabled: isExportsDisabled,
          type: SettingsType.EXPORT,
          disabledMessage: exportsDisabledMessage,
          ...exports,
        },
  ].filter(isTruthy);

export { getRightMenuConfig };
